import { DEFAULT_LAYOUT } from "./widgetRegistry";

/**
 * Dashboard templates — ready-made layouts an admin can drop onto a workspace
 * in one click. Every template is a plain layout array on the SAME 24-column /
 * 32px-gutter grid as the default board, so a template can never look different
 * from a hand-built board — it inherits the exact spacing.
 *
 * Order matters: Technician board is the default and leads the picker
 * (recommended). Sales is one option further down, never the headline.
 *
 * Blank canvas and "keep current" are handled as special actions in the
 * picker, not as entries here.
 */
export const TEMPLATES = [
  {
    id: "inhouse",
    name: "In-House Sales",
    blurb: "Mountain, the Yeti, earnings and milestones",
    recommended: true,
    // The four cards an in-house rep lives on. Asymmetric rows (wide side
    // alternates) so it reads as a dashboard, not a 2×2 grid.
    layout: [
      { i: "kotm", x: 0, y: 0, w: 14, h: 13 },
      { i: "beatyeti", x: 14, y: 0, w: 10, h: 13 },
      { i: "earnings", x: 0, y: 13, w: 10, h: 14 },
      { i: "milestones", x: 10, y: 13, w: 14, h: 14 },
    ],
  },
  {
    id: "field",
    name: "Field team",
    blurb: "Mountain, the Yeti, earnings and self-generated",
    // Same spine as In-House, but the fourth card is the rep's SELF-GENERATED
    // work instead of the milestone ladder. That one is a "Build your own"
    // widget, so it arrives named and sized with its slot ready; the admin picks
    // the two Housecall Pro metrics and switches on the commission, because
    // those metric ids are per-workspace and cannot be baked into a template.
    layout: [
      { i: "kotm", x: 0, y: 0, w: 14, h: 13 },
      { i: "beatyeti", x: 14, y: 0, w: 10, h: 13 },
      /* The field card is slim: a rep in a driveway wants what they made today,
         not a milestone ladder, their personal best and the team leader. Those
         three chips are the In-House framing — switched off here, which also
         takes ~90px off the card so the row it shares stops being so tall. */
      { i: "earnings", x: 0, y: 13, w: 10, h: 14, s: { hide: { milestone: true, best: true, top: true } } },
      { i: "custom-selfgen", x: 10, y: 13, w: 14, h: 14, s: { title: "Self-generated" } },
    ],
  },
  {
    id: "technician",
    name: "Technician board",
    blurb: "The classic field-team layout",
    layout: DEFAULT_LAYOUT,
  },
  {
    id: "sales",
    name: "Sales scorecard",
    blurb: "Earnings, milestones and the Yeti challenge",
    // The JT artifact, 1:1 — a greeting header, then the two rows it shows:
    // Goal of the Mountain + Beat the Yeti, then Anticipated earnings +
    // Milestone bonuses. No leaderboard / podium / widgets — this is a
    // focused sales scorecard, not the technician board.
    // Asymmetric rows (like the default dashboard's Hero+Widgets / Leaderboard+
    // Podium) — NOT equal halves. Wide side alternates row to row so it reads
    // as a real dashboard, not a 2×2 grid.
    layout: [
      { i: "hero", x: 0, y: 0, w: 24, h: 15 },
      { i: "kotm", x: 0, y: 15, w: 14, h: 13 },
      { i: "beatyeti", x: 14, y: 15, w: 10, h: 13 },
      { i: "earnings", x: 0, y: 28, w: 10, h: 12 },
      { i: "milestones", x: 10, y: 28, w: 14, h: 12 },
    ],
  },
  {
    id: "teamtv",
    name: "Team TV",
    blurb: "Big leaderboard for the wall screen",
    layout: [
      { i: "leaderboard", x: 0, y: 0, w: 16, h: 20 },
      { i: "podium", x: 16, y: 0, w: 8, h: 20 },
      { i: "kotm", x: 0, y: 20, w: 24, h: 13 },
    ],
  },
  {
    id: "minimal",
    name: "Minimal",
    blurb: "Just the greeting and the board",
    layout: [
      { i: "hero", x: 0, y: 0, w: 24, h: 15 },
      { i: "leaderboard", x: 0, y: 15, w: 24, h: 13 },
    ],
  },
];

export const templateById = (id) => TEMPLATES.find((t) => t.id === id) || null;
